import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Paper, TextField, Typography, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText,
  InputAdornment, CircularProgress, alpha, useTheme,
  Fade
} from '@mui/material';
import {
  Search, ChevronRight,
  ArrowBackIosNew, Folder, Person, Settings
} from '@mui/icons-material';

const TYPE_CONFIG = {
  project: { icon: <Folder sx={{ fontSize: 18, color: '#fbbf24' }} />, label: 'Project' },
  client:  { icon: <Person  sx={{ fontSize: 18, color: '#3b82f6' }} />, label: 'Client'  },
  service: { icon: <Settings sx={{ fontSize: 18, color: '#f59e0b' }} />, label: 'Service' },
};

export default function SearchDropdown({
  onSelect,
  // Accept both flat arrays (from getLocalProjectsData) and the legacy structuredData shape
  projects: projectsProp = [],
  clients:  clientsProp  = [],
  services: servicesProp = [],
  structuredData,
  loading,
}) {
  const theme = useTheme();

  // Normalise: if caller passes structuredData object, unpack it
  const projects = structuredData?.projects ?? projectsProp;
  const clients  = structuredData?.clients  ?? clientsProp;

  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState('project'); // 'project' | 'client' | 'service'
  const [searchTerm, setSearchTerm] = useState('');
  const [history, setHistory] = useState([]);

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedClient,  setSelectedClient]  = useState(null);

  const dropdownRef = useRef(null);
  const inputRef    = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current    && !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
        reset();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const reset = () => {
    setCurrentView('project');
    setSelectedProject(null);
    setSelectedClient(null);
    setHistory([]);
  };

  const handleBack = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setCurrentView(prev.view);
    setSelectedProject(prev.project);
    setSelectedClient(prev.client);
  };

  const navigateTo = (view, updates = {}) => {
    setHistory([...history, { view: currentView, project: selectedProject, client: selectedClient }]);
    if (updates.project !== undefined) setSelectedProject(updates.project);
    if (updates.client  !== undefined) setSelectedClient(updates.client);
    setCurrentView(view);
    setSearchTerm('');
  };

  const handleItemSelect = (item) => {
    if (item.type === 'project') {
      navigateTo('client', { project: item });
    } else if (item.type === 'client') {
      navigateTo('service', { client: item });
    } else if (item.type === 'service') {
      // Final selection — build path levels
      onSelect(
        selectedProject,   // pathLevel1 = project.name
        selectedClient,    // pathLevel2 = client.name
        item,              // pathLevel3 = service.service
        null, null, null
      );
      setIsOpen(false);
      reset();
    }
  };

  const items = useMemo(() => {
    const q = searchTerm.toLowerCase();

    if (currentView === 'project') {
      const list = projects.map(p => ({ ...p, type: 'project' }));
      return q ? list.filter(i => (i.name || '').toLowerCase().includes(q)) : list;
    }

    if (currentView === 'client') {
      // Pull clients that belong to the selected project
      const projectClients = clients
        .filter(c => c.project_id === selectedProject?.id)
        .map(c => ({ ...c, type: 'client' }));
      return q
        ? projectClients.filter(i => (i.name || '').toLowerCase().includes(q))
        : projectClients;
    }

    if (currentView === 'service') {
      // Services live inside the client object in the JSON
      // selectedClient may come from the flat `clients` map — locate the raw
      // client entry in clients prop to get its Services array.
      const rawClient = clients.find(c => c.id === selectedClient?.id);
      // Support both capitalised and lowercase keys
      const svcs = rawClient?.Services ?? rawClient?.services ?? [];
      const svcItems = svcs.map(s => ({
        ...s,
        name: s.service,
        type: 'service',
      }));
      return q ? svcItems.filter(i => (i.name || '').toLowerCase().includes(q)) : svcItems;
    }

    return [];
  }, [currentView, searchTerm, projects, clients, selectedProject, selectedClient]);

  const breadcrumb = useMemo(() => {
    const parts = [];
    if (selectedProject) parts.push(selectedProject.name);
    if (selectedClient)  parts.push(selectedClient.name);
    return parts.join(' › ');
  }, [selectedProject, selectedClient]);

  const placeholderMap = {
    project: 'Search project…',
    client:  'Search client…',
    service: 'Search service…',
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        inputRef={inputRef}
        placeholder={placeholderMap[currentView] || 'Search…'}
        autoComplete="off"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClick={() => setIsOpen(true)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: 'text.secondary', fontSize: 18 }} />
            </InputAdornment>
          ),
          sx: {
            borderRadius: 2,
            bgcolor: '#fff',
            '& fieldset': { borderColor: '#d1d5db' },
            '&:hover fieldset': { borderColor: theme.palette.primary.main },
            '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 1 },
          },
        }}
      />

      <Fade in={isOpen}>
        <Paper
          ref={dropdownRef}
          elevation={8}
          sx={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1200,
            maxHeight: 400, overflowY: 'auto', borderRadius: 1.5, border: '1px solid #e5e7eb',
            bgcolor: '#fff',
          }}
        >
          {/* Breadcrumb / Back bar */}
          {history.length > 0 && (
            <Box sx={{ p: 1, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ListItemButton onClick={handleBack} sx={{ py: 0.5, px: 1, borderRadius: 1, flexShrink: 0 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <ArrowBackIosNew sx={{ fontSize: 12 }} />
                </ListItemIcon>
                <ListItemText primary="Back" primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }} />
              </ListItemButton>
              {breadcrumb && (
                <Typography variant="caption" noWrap sx={{ color: '#6b7280', ml: 0.5 }}>
                  {breadcrumb}
                </Typography>
              )}
            </Box>
          )}

          {loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={18} />
            </Box>
          ) : (
            <List sx={{ py: 0.5 }}>
              {items.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', color: 'text.disabled' }}>
                  <Typography variant="body2">No results found</Typography>
                </Box>
              ) : items.map((item, idx) => {
                const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.project;
                const hasChildren = item.type === 'project' || item.type === 'client';
                return (
                  <ListItem key={`${item.type}-${item.id ?? idx}`} disablePadding>
                    <ListItemButton onClick={() => handleItemSelect(item)} sx={{ px: 2, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {config.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{ variant: 'body2', color: '#374151', fontWeight: 500 }}
                      />
                      {hasChildren && (
                        <ChevronRight sx={{ fontSize: 16, color: '#9ca3af' }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      </Fade>
    </Box>
  );
}

